import React from 'react'
import { Tooltip } from '@mui/material'
import { SaveOutlined, RestartAltRounded, CheckCircleRounded, WarningAmberRounded, DownloadRounded, Search as SearchIcon, HelpOutlineRounded } from '@mui/icons-material'
import { api } from '../../function/axios'
import { ConfirmV2 } from '../ConfirmV2'
import s from './SystemV2.module.css'

/*
 * A System section of settings set here instead of the ConfigMap — CRM
 * (CrmSettingsV2) and Search timeouts (SearchTimeoutsV2) are both this, each
 * showing its own groups of keys. The backend's calls: GET /system-settings (every key with its value and
 * where it comes from), PATCH /system-settings/:key {value}, DELETE
 * /system-settings/:key (back to the ConfigMap). A value saved here wins
 * over the ConfigMap and is used without a restart. "Copy from ConfigMap"
 * stores the ConfigMap values as they are, so they can be edited here
 * without having to change them first — it is the same PATCH per key. Every
 * change goes to admin_audit, secrets masked.
 */

export type Setting = {
  key: string
  group: string
  label: string
  secret: boolean
  options: string[] | null
  integer: boolean
  /** an http(s) address — base: the backend appends paths, so no slash at the end; query: it appends ?params, so none here; full: used as it is */
  url: 'base' | 'query' | 'full' | null
  email: boolean
  min: number | null
  max: number | null
  unit: string | null
  /** the search types that read the key — shown under it, and what the search box looks through */
  usedBy: string[] | null
  /** the workflows (lookups) of those search types — listed when saving; via = the only path that reads the key */
  workflows: { id: number; title: string; via: string | null }[] | null
  /** what the key does — the "?" next to its name */
  help: string | null
  value: string | null
  source: 'db' | 'env' | null
  envValue: string | null
  updatedBy: string | null
  updatedAt: string | null
}

export type SettingsGroup = { key: string; title: string; sub: string }

/* 90 → "1 h 30 min" */
const minutes = (n: number) => (n < 60 ? `${n} min` : n % 60 ? `${Math.floor(n / 60)} h ${n % 60} min` : `${n / 60} h`)

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')
const errText = (e: any) => {
  const m = e?.response?.data?.message
  return (Array.isArray(m) ? m.join(', ') : typeof m === 'string' ? m : e?.message) || 'Something went wrong'
}

export function SettingsSectionV2({ title, intro, groups, layout = 'grid', saveNote }: {
  title: string
  intro: string
  groups: SettingsGroup[]
  /** grid: two fields a line; rows: one key a line, with what uses it */
  layout?: 'grid' | 'rows'
  /** the Save all confirm's last sentence, for the keys being saved */
  saveNote?: (keys: string[]) => string
}) {
  const [list, setList] = React.useState<Setting[] | null>(null)
  const [drafts, setDrafts] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState<string | null>(null)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [toSave, setToSave] = React.useState(false)
  const [toCopy, setToCopy] = React.useState(false)
  const [toReset, setToReset] = React.useState<Setting | null>(null)
  const [query, setQuery] = React.useState('')

  /* the backend answers with every key; this section keeps its own groups */
  const take = (data: any) => {
    const ours = new Set(groups.map((g) => g.key))
    const rows: Setting[] = (Array.isArray(data) ? data : []).filter((r: Setting) => ours.has(r.group))
    setList(rows)
    setDrafts(Object.fromEntries(rows.map((r) => [r.key, r.value ?? ''])))
  }

  React.useEffect(() => {
    api({ method: 'GET', url: '/system-settings' })
      .then(({ data }) => take(data))
      .catch((e) => { setList([]); setNote({ kind: 'err', text: errText(e) }) })
  // once, on open — a section's groups never change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const draftOf = (r: Setting) => drafts[r.key] ?? ''
  const dirty = (r: Setting) => draftOf(r).trim() !== (r.value ?? '')
  const problem = (r: Setting, value?: string): string | null => {
    const v = (value ?? draftOf(r)).trim()
    if (!v) return 'Cannot be empty'
    if (r.integer && !/^\d+$/.test(v)) return 'A whole number'
    if (r.url === 'base' && !/^https?:\/\/\S+[^/]$/.test(v)) return 'An http(s) address, no slash at the end'
    if (r.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'An email address'
    if (r.url === 'query' && !/^https?:\/\/[^\s?#]+$/.test(v)) return 'An http(s) address, no parameters'
    if (r.url === 'full' && !/^https?:\/\/\S+$/.test(v)) return 'An http(s) address'
    if ((r.min !== null && Number(v) < r.min) || (r.max !== null && Number(v) > r.max)) return `From ${r.min ?? '…'} to ${r.max ?? '…'}`
    if (r.options && !r.options.includes(v)) return `One of ${r.options.join(', ')}`
    return null
  }

  const changed = (list || []).filter(dirty)
  const invalid = changed.some((r) => problem(r))

  /* still read from the ConfigMap — "Copy from ConfigMap" stores these; a value the panel would refuse is left out */
  const fromEnv = (list || []).filter((r) => r.source === 'env' && r.envValue !== null)
  const copyable = fromEnv.filter((r) => !problem(r, r.envValue ?? ''))
  const notCopyable = fromEnv.filter((r) => problem(r, r.envValue ?? ''))

  /* one PATCH per key, in turn — the last answer is the whole list */
  async function put(entries: { key: string; value: string }[]) {
    setBusy('all')
    const done: string[] = []
    try {
      let data: any = null
      for (const e of entries) {
        const res = await api({ method: 'PATCH', url: `/system-settings/${e.key}`, body: { value: e.value.trim() } })
        data = res.data
        done.push(e.key)
      }
      if (data) take(data)
      setNote({ kind: 'ok', text: `Saved ${done.length === 1 ? done[0] : `${done.length} settings`} — in use from the next request` })
    } catch (e) {
      // what was saved stays saved; reload so the form shows it, keep the rest of the drafts
      const left = Object.fromEntries(changed.filter((r) => !done.includes(r.key)).map((r) => [r.key, draftOf(r)]))
      try {
        const { data } = await api({ method: 'GET', url: '/system-settings' })
        take(data)
        setDrafts((d) => ({ ...d, ...left }))
      } catch { /* the form keeps what it had */ }
      setNote({ kind: 'err', text: `${done.length ? `Saved ${done.join(', ')}; ` : ''}${errText(e)}` })
    } finally {
      setBusy(null)
    }
  }
  const saveAll = () => put(changed.map((r) => ({ key: r.key, value: draftOf(r) })))
  const copyFromEnv = () => put(copyable.map((r) => ({ key: r.key, value: r.envValue ?? '' })))

  async function reset(r: Setting) {
    setBusy(r.key)
    try {
      const { data } = await api({ method: 'DELETE', url: `/system-settings/${r.key}` })
      take(data)
      setNote({ kind: 'ok', text: `${r.key} is back to the ConfigMap value` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setBusy(null)
    }
  }

  /* what a change reaches: each key with its workflows, for the Save all and Reset confirms */
  function reach(rows: { r: Setting; from: string | null; to: string | null }[]) {
    const withWf = rows.flatMap(({ r, from, to }) => (r.workflows ? [{ r, from, to, wf: r.workflows }] : []))
    if (!withWf.length) return null
    return (
      <div className={s.reach}>
        {withWf.map(({ r, from, to, wf }) => (
          <div key={r.key}>
            <b>{r.label}</b>
            <span className={s.muted}> {from ?? '—'} → {to ?? '—'}{r.unit ? ` ${r.unit}` : ''}</span>
            {wf.length ? (
              <div className={s.usedBy}>
                {wf.map((w) => <span key={w.id}>{w.title}{w.via && <i> · {w.via} only</i>}</span>)}
              </div>
            ) : <div className={s.hint}>No workflow uses it</div>}
          </div>
        ))}
      </div>
    )
  }
  const wfCount = (rows: Setting[]) => new Set(rows.flatMap((r) => (r.workflows || []).map((w) => w.id))).size

  const q = query.trim().toLowerCase()
  const hit = (t: string) => !!q && t.toLowerCase().includes(q)
  const matches = (r: Setting) => !q || hit(r.label) || hit(r.key) || !!r.usedBy?.some(hit)

  function field(r: Setting) {
    const bad = dirty(r) ? problem(r) : null
    const off = busy !== null
    const n = Number(draftOf(r).trim())
    const label = (
      <span className={s.setLabel}>
        <b>{r.label}</b>
        {r.help && (
          <Tooltip title={<span className={s.helpTip}>{r.help}</span>} placement="top" arrow>
            <span className={s.help} tabIndex={0} aria-label={`About ${r.label}`} onClick={(e) => e.preventDefault()}>
              <HelpOutlineRounded sx={{ fontSize: 15 }} />
            </span>
          </Tooltip>
        )}
        <span className={`${s.srcTag} ${r.source === 'db' ? s.srcDb : r.source === 'env' ? s.srcEnv : s.srcNone}`}>
          {r.source === 'db' ? 'Set here' : r.source === 'env' ? 'ConfigMap' : 'Not set'}
        </span>
        {dirty(r) && <span className={s.srcEdited}>Edited</span>}
        <span className={s.spacer} />
        {r.source === 'db' && !dirty(r) && (
          <Tooltip title={r.envValue === null ? 'Back to the ConfigMap — it has no value for this key' : 'Back to the ConfigMap value'}>
            <button type="button" className={s.setReset} onClick={(e) => { e.preventDefault(); setToReset(r) }} disabled={off} aria-label="Reset to ConfigMap">
              <RestartAltRounded sx={{ fontSize: 15 }} />
            </button>
          </Tooltip>
        )}
      </span>
    )
    const input = r.options ? (
      <select className={s.setInput} value={draftOf(r)} onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))} disabled={off}>
        {!r.options.includes(draftOf(r)) && <option value={draftOf(r)}>{draftOf(r) || '— not set —'}</option>}
        {r.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input
        className={`${s.setInput} ${bad ? s.setInputBad : ''}`}
        value={draftOf(r)}
        inputMode={r.integer ? 'numeric' : undefined}
        placeholder="Not set"
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
        disabled={off}
      />
    )
    const hint = bad
      ? <span className={s.err}>{bad}</span>
      : <span className={s.hint}>{r.key}{r.source === 'db' && r.updatedAt ? ` · ${r.updatedBy ? `${r.updatedBy}, ` : ''}${when(r.updatedAt)}` : ''}</span>

    if (layout === 'grid') return <label className={s.setField}>{label}{input}{hint}</label>
    return (
      <>
        <div className={s.setField}>
          {label}
          {hint}
          {!!r.usedBy?.length && (
            <div className={s.usedBy}>
              {r.usedBy.map((t) => <span key={t} className={hit(t) ? s.usedHit : undefined}>{t}</span>)}
            </div>
          )}
        </div>
        <div className={s.setValue}>
          <div className={s.setUnit}>{input}{r.unit && <span>{r.unit}</span>}</div>
          {r.unit === 'min' && !bad && Number.isInteger(n) && n > 0 && <span className={s.hint}>= {minutes(n)}</span>}
        </div>
      </>
    )
  }

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <b>{title}</b>
          <span>{intro}</span>
        </div>
        {copyable.length > 0 && (
          <Tooltip title={changed.length ? 'Save or discard your changes first' : 'Store the ConfigMap values here as they are, to edit them in the panel'}>
            <span>
              <button type="button" className={s.ghostBtn} onClick={() => setToCopy(true)} disabled={!!changed.length || busy !== null}>
                <DownloadRounded sx={{ fontSize: 16 }} />Copy from ConfigMap ({copyable.length})
              </button>
            </span>
          </Tooltip>
        )}
      </div>

      {note && (
        <div className={`${s.notice} ${note.kind === 'err' ? s.noticeErr : s.noticeOk}`}>
          {note.kind === 'err' ? <WarningAmberRounded sx={{ fontSize: 17 }} /> : <CheckCircleRounded sx={{ fontSize: 17 }} />}
          <span>{note.text}</span>
          <button type="button" onClick={() => setNote(null)} aria-label="Close">×</button>
        </div>
      )}

      {list === null && <div className={s.empty}>Loading…</div>}

      {list && list.some((r) => r.usedBy?.length) && (
        <div className={s.search}>
          <SearchIcon sx={{ fontSize: 17, color: 'var(--text3)' }} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find by search type — adid, phone, FaceSearch…" />
          {query && <button type="button" className={s.setReset} onClick={() => setQuery('')} aria-label="Clear">×</button>}
        </div>
      )}

      {list && list.length > 0 && groups.map((g) => {
        const rows = list.filter((r) => r.group === g.key && matches(r))
        if (!rows.length) return null
        return (
          <div key={g.key} className={s.card}>
            <div className={s.cardHead}>
              <b>{g.title}</b>
              <span className={s.muted}>{g.sub}</span>
            </div>
            {layout === 'rows'
              ? <div className={s.setRows}>{rows.map((r) => <div key={r.key} className={s.setRow}>{field(r)}</div>)}</div>
              : <div className={s.setGrid}>{rows.map((r) => <React.Fragment key={r.key}>{field(r)}</React.Fragment>)}</div>}
          </div>
        )
      })}
      {list && list.length > 0 && q && !list.some(matches) && <div className={s.empty}>No setting is used by “{query.trim()}”</div>}

      {list && list.length > 0 && (
        <div className={s.saveBar}>
          <span className={s.muted}>
            {changed.length ? `${changed.length} unsaved ${changed.length === 1 ? 'change' : 'changes'}` : 'No changes'}
          </span>
          <span className={s.spacer} />
          <button type="button" className={s.ghostBtn} onClick={() => setDrafts(Object.fromEntries(list.map((r) => [r.key, r.value ?? ''])))} disabled={!changed.length || busy !== null}>
            Discard
          </button>
          <button type="button" className={s.primary} onClick={() => setToSave(true)} disabled={!changed.length || invalid || busy !== null}>
            <SaveOutlined sx={{ fontSize: 16 }} />{busy === 'all' ? 'Saving…' : 'Save all'}
          </button>
        </div>
      )}

      {toSave && (
        <ConfirmV2
          header={`Save ${changed.length === 1 ? changed[0].key : `${changed.length} settings`}?${changed.some((r) => r.workflows) ? ` It reaches ${wfCount(changed)} ${wfCount(changed) === 1 ? 'workflow' : 'workflows'}.` : ''}`}
          yesLabel="Save all"
          onYes={() => { setToSave(false); void saveAll() }}
          onClose={() => setToSave(false)}
        >
          {changed.map((r) => r.key).join(', ')}.{' '}
          {saveNote?.(changed.map((r) => r.key)) ?? 'The backend uses them from the next request.'}
          {reach(changed.map((r) => ({ r, from: r.value, to: draftOf(r).trim() })))}
        </ConfirmV2>
      )}
      {toCopy && (
        <ConfirmV2
          header={`Copy ${copyable.length === 1 ? copyable[0].key : `${copyable.length} settings`} from the ConfigMap?`}
          yesLabel="Copy"
          onYes={() => { setToCopy(false); void copyFromEnv() }}
          onClose={() => setToCopy(false)}
        >
          {copyable.map((r) => r.key).join(', ')} — stored here with the values they have now, nothing changes for the backend.
          {notCopyable.length > 0 && ` Left out: ${notCopyable.map((r) => `${r.key} (${problem(r, r.envValue ?? '')})`).join(', ')} — set ${notCopyable.length === 1 ? 'it' : 'them'} by hand.`}
        </ConfirmV2>
      )}
      {toReset && (
        <ConfirmV2
          header={`Reset ${toReset.key} to the ConfigMap?`}
          yesLabel="Reset"
          onYes={() => { const r = toReset; setToReset(null); void reset(r) }}
          onClose={() => setToReset(null)}
        >
          {toReset.envValue === null
            ? 'The ConfigMap has no value for this key — after the reset it is not set at all.'
            : 'The value set here is removed and the ConfigMap one is used again.'}
          {reach([{ r: toReset, from: toReset.value, to: toReset.envValue }])}
        </ConfirmV2>
      )}
    </div>
  )
}
